/**
 * Coup de pouce — Attendu 1061 « trouver le nombre d'éléments pour une étape
 * donnée dans une suite de motifs évolutive ». UNE page STATIQUE, partagée par
 * les trois generator_keys. Contenu validé en texte nu par le prof le
 * 2026-09-10, codé mot pour mot.
 *
 * L'ORDRE DU RAISONNEMENT, arbitré ce jour-là : d'abord ce qui s'ajoute et
 * OÙ, en comparant deux paires d'étapes voisines (une seule ne prouve pas que
 * l'ajout est constant) ; ensuite le point de départ, la DERNIÈRE étape
 * dessinée et non l'étape 1 (plus court, moins d'erreurs) ; enfin l'avancée
 * jusqu'au rang demandé. Les DEUX gestes sont écrits séparément (« le même
 * paquet de 4 revient » puis « je pars de 13 ») : un élève peut réussir le
 * premier et rater le second, et les fusionner masquerait lequel manque.
 *
 * Le support est un TABLEAU NUMÉROTÉ, et c'est ce qui fait sa valeur : il
 * supprime le comptage des bonds, première source d'erreur (« de l'étape 3 à
 * l'étape 8, y a-t-il 5 bonds ou 8 ? »). L'élève s'arrête à la colonne
 * demandée. On organise l'itération sans la remplacer, et sans rien donner de
 * la structure, qui est 1062.
 *
 * L'image que l'élève a déjà : le carrelage de la fiche de 638 (5 ; 9 ; 13) et
 * la ligne de nombres à « + 4 » (Eduscol patterns p. 9, élève 1). Fiche voisine
 * de celle de 1060, même principe, autre arrêt : 1060 montre l'étape suivante,
 * 1061 va jusqu'au rang demandé.
 * Retiré le 2026-09-10 : le calcul « Étape 10 : 5 + 9 × 4 = 41 » et sa ligne
 * « il y a 9 flèches » — c'était la stratégie des sauts groupés, qui n'a plus
 * lieu d'être depuis que les rangs sont bornés à 8.
 *
 * SACRIFIÉ : le couple (fixe 1, groupe 4), la suite 5 ; 9 ; 13, exclu de
 * toutes les cartes de 1060 et 1061 (cdp1061.self.test.js le vérifie sur
 * l'image rendue). Largeur bornée à 340 px, tailles de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#94A3B8';

const svg = (aria, hauteur, corps) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 ${hauteur}" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  ${corps}
</svg>`.trim();

// ─── 1. Les trois rangées : ce qui s'ajoute, et qu'il s'ajoute deux fois ──

const C = 22;
const PAS = 24;
const X0 = 72;
const RANGEES = [5, 9, 13].map((v, i) => {
    const y = 10 + i * 30;
    const cases = Array.from({ length: v }, (_, j) => {
        const ajoute = i > 0 && j >= v - 4;
        return `<rect x="${X0 + j * PAS}" y="${y}" width="${C}" height="${C}" rx="2" fill="${ajoute ? ORANGE : BLEU}"/>`;
    }).join('');
    return `<text x="8" y="${y + 16}" font-size="13" font-weight="bold" fill="${ENCRE}">Étape ${i + 1}</text>${cases}`;
}).join('\n  ');

// ─── 2. Le tableau numéroté : l'élève avance jusqu'à sa colonne ───────────

const X_TAB = 46;
const LARGEUR_COL = 52;
const Y_HAUT = 144;
const Y_BAS = 186;
const COLONNES = [1, 2, 3, 4, 5, 6].map((n, i) => {
    const x = X_TAB + i * LARGEUR_COL + LARGEUR_COL / 2;
    const valeur = 5 + i * 4;
    const trouve = n > 3;
    return `<text x="${x}" y="${Y_HAUT - 10}" font-size="15" text-anchor="middle" fill="${ENCRE}">${n}</text>`
        + `<text x="${x}" y="${Y_BAS - 13}" font-size="19" font-weight="bold" text-anchor="middle" fill="${trouve ? ORANGE : BLEU}">${valeur}</text>`
        + (i < 5 ? `<text x="${x + LARGEUR_COL / 2}" y="${Y_BAS + 15}" font-size="13" font-weight="bold" text-anchor="middle" fill="${ORANGE}">+ 4</text>` : '');
}).join('\n  ');
const CADRE = `<line x1="${X_TAB}" y1="${Y_HAUT}" x2="${X_TAB + 6 * LARGEUR_COL}" y2="${Y_HAUT}" stroke="${GRIS}" stroke-width="1"/>`
    + [0, 1, 2, 3, 4, 5, 6].map((i) => `<line x1="${X_TAB + i * LARGEUR_COL}" y1="${Y_HAUT}" x2="${X_TAB + i * LARGEUR_COL}" y2="${Y_BAS}" stroke="${GRIS}" stroke-width="1"/>`).join('')
    + `<line x1="${X_TAB}" y1="${Y_BAS}" x2="${X_TAB + 6 * LARGEUR_COL}" y2="${Y_BAS}" stroke="${GRIS}" stroke-width="1"/>`
    + `<text x="${X_TAB - 6}" y="${Y_HAUT - 10}" font-size="14" text-anchor="end" fill="${ENCRE}">Étape</text>`;

const PAGE = svg(
    'Trois rangées de carreaux : étape 1, 5 carreaux ; étape 2, 9 carreaux ; étape 3, 13 carreaux. À l\'étape 2 comme à l\'étape 3, les 4 carreaux qui viennent d\'être ajoutés sont en orange, au même endroit. Dessous, un tableau des étapes 1 à 6 : 5, 9, 13, puis 17, 21, 25 en orange, avec plus 4 d\'une colonne à la suivante. Le même paquet de 4 revient à chaque étape. Je pars de l\'étape 3 : 13, puis 4 en 4.',
    268,
    `<!-- 1. les trois rangées : les 4 ajoutés en orange, deux fois de suite -->
  ${RANGEES}

  <!-- 2. le tableau numéroté : plus besoin de compter les bonds -->
  ${CADRE}
  ${COLONNES}

  <!-- 3. les DEUX gestes, séparés : d'où vient le 4, puis comment j'avance -->
  <text x="200" y="228" font-size="15" text-anchor="middle" fill="${ENCRE}">Le même paquet de <tspan font-weight="bold" fill="${ORANGE}">4</tspan> revient à chaque étape.</text>
  <text x="200" y="252" font-size="15" text-anchor="middle" fill="${ENCRE}">Je pars de l'étape 3 : 13, puis 4 en 4.</text>`,
);

const AIDE = { pages: [{ titre: 'Avancer d\'étape en étape', svg: PAGE }] };

const CP_1061 = {
    flash_texte_trou_saisie: AIDE,
    combien_qcm_unique: AIDE,
    combien_tableau_vf: AIDE,
};

export default CP_1061;
